import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';

export interface Crumb {
  label: string;
  href?: string;
}

interface BreadcrumbContextValue {
  crumbs: readonly Crumb[];
  register: (id: string, crumb: Crumb) => void;
  unregister: (id: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

/**
 * Pages register the crumbs the route cannot derive (a record name, a
 * settings sub-page). The shell renders them after the section and page
 * crumbs it derives from the navigation model.
 */
export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<Map<string, Crumb>>(() => new Map());

  const register = useCallback((id: string, crumb: Crumb) => {
    setEntries((previous) => {
      const current = previous.get(id);
      if (current && current.label === crumb.label && current.href === crumb.href) return previous;
      const next = new Map(previous);
      next.set(id, crumb);
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((previous) => {
      if (!previous.has(id)) return previous;
      const next = new Map(previous);
      next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ crumbs: Array.from(entries.values()), register, unregister }),
    [entries, register, unregister],
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** The registered crumbs; empty outside the provider so components render in isolation. */
export function useRegisteredCrumbs(): readonly Crumb[] {
  return useContext(BreadcrumbContext)?.crumbs ?? [];
}

/**
 * Registers one crumb (a record name, a sub-page) while the component is
 * mounted. No-op outside the provider. `label` empty registers nothing, so a
 * page can pass the name once the record has loaded.
 */
export function useRegisterBreadcrumb(label: string | undefined, href?: string): void {
  const context = useContext(BreadcrumbContext);
  const id = useId();
  // The callbacks are stable; depending on the context value itself would
  // re-run the effect after every register (the value changes), loop forever.
  const register = context?.register;
  const unregister = context?.unregister;

  useEffect(() => {
    if (!register || !unregister || !label) return;
    register(id, { label, href });
    return () => unregister(id);
  }, [register, unregister, id, label, href]);
}
